import {AxiosError} from 'axios'

import {
  PLANT_ACTIONS_BULK_UPLOAD_ACCEPTED_EXTENSION,
  PLANT_ACTIONS_BULK_UPLOAD_COLUMNS,
  PLANT_ACTIONS_BULK_UPLOAD_DELIMITER
} from '../consts'
import {
  BulkUploadPlantActionsFailure,
  BulkUploadPlantActionsGroupedRowError,
  BulkUploadPlantActionsProblem,
  BulkUploadPlantActionsRowError
} from '../types'

// BOM so Excel opens the file as UTF-8 (plant and user names may contain non-ASCII characters)
const UTF8_BOM = '﻿'

// The backend counts the header as row 1, so row numbers match the spreadsheet line numbers
export const BULK_UPLOAD_HEADER_ROW_NUMBER = 1

export const getMandatoryBulkUploadColumnNames = (): string[] =>
  PLANT_ACTIONS_BULK_UPLOAD_COLUMNS.filter(({isMandatory}) => isMandatory).map((column) =>
    'format' in column ? `${column.name} (${column.format})` : column.name
  )

export const createPlantActionsBulkUploadTemplate = (): Blob => {
  const header = PLANT_ACTIONS_BULK_UPLOAD_COLUMNS.map(({name, isMandatory}) =>
    isMandatory ? `${name}*` : name
  ).join(PLANT_ACTIONS_BULK_UPLOAD_DELIMITER)

  return new Blob([`${UTF8_BOM}${header}\n`], {type: 'text/csv;charset=utf-8'})
}

export const isPlantActionsBulkUploadFile = (file: File): boolean =>
  file.name.toLowerCase().endsWith(PLANT_ACTIONS_BULK_UPLOAD_ACCEPTED_EXTENSION)

export const groupBulkUploadRowErrors = (
  errors: BulkUploadPlantActionsRowError[]
): BulkUploadPlantActionsGroupedRowError[] => {
  const reasonsByRow = new Map<number, string[]>()
  errors.forEach(({rowNumber, reason}) => {
    reasonsByRow.set(rowNumber, [...(reasonsByRow.get(rowNumber) ?? []), reason])
  })
  return Array.from(reasonsByRow, ([rowNumber, reasons]) => ({rowNumber, reasons})).sort(
    (a, b) => a.rowNumber - b.rowNumber
  )
}

export const getBulkUploadFailure = (
  error: AxiosError<BulkUploadPlantActionsProblem>
): BulkUploadPlantActionsFailure => {
  const status = error.response?.status
  const problem = error.response?.data

  if (status === 400) {
    const rowErrors = problem?.data?.errors ?? []
    return rowErrors.length
      ? {kind: 'rows', rows: groupBulkUploadRowErrors(rowErrors), errorCount: rowErrors.length}
      : {kind: 'detail', detail: problem?.detail}
  }
  if (status === 401) return {kind: 'unauthorized'}
  if (status === 403) return {kind: 'forbidden'}
  return {kind: 'unexpected'}
}
